import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MInputField from "@/components/ui/atoms/MInputField";
import MDateInputField from "@/components/ui/molecules/MDateInputField";
import { MButton } from "@/components/ui/atoms/MButtons";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { useProfileField } from "@/hooks/useHealthProfile";
import { getMemberInfo } from "@/hooks/useMembers";
import FormHeader from "@/components/ui/organisms/MHeader";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useTranslation } from "react-i18next";
import { MedicalDetailType } from "@/types/health-profile";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import { ISurgery } from "@/types/surgeries";
import { sortByYearMonth } from "@/utils/date-utils";

const SurgeriesForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const { fieldValue: surgeries = [], saveField: saveSurgeries } =
    useProfileField<ISurgery[]>(memberId, MedicalDetailType.SURGERIES, []);

  // For edit mode: /SurgeriesForm?mode=edit&index=0
  const editingSurgery = location.state?.surgery;
  const index = location.state?.idx;
  const [surgeryName, setSurgeryName] = useState(editingSurgery?.name || "");
  const [surgeryYear, setSurgeryYear] = useState<number | null>(
    editingSurgery?.year || null
  );
  const [surgeryMonth, setSurgeryMonth] = useState<number | null>(
    editingSurgery?.month || null
  );
  const [loading, setLoading] = useState(false);

  // Track initial values for dirty check in edit mode
  const [initialSurgeryName, setInitialSurgeryName] = useState(
    editingSurgery?.name || ""
  );
  const [initialSurgeryYear, setInitialSurgeryYear] = useState<number | null>(
    editingSurgery?.year || null
  );
  const [initialSurgeryMonth, setInitialSurgeryMonth] = useState<number | null>(
    editingSurgery?.month || null
  );

  // Ensure fields are prefilled when editing surgery changes (for edit mode)
  useEffect(() => {
    if (editingSurgery) {
      setSurgeryName(editingSurgery.name || "");
      setSurgeryYear(editingSurgery.year || null);
      setSurgeryMonth(editingSurgery.month || null);
      setInitialSurgeryName(editingSurgery.name || "");
      setInitialSurgeryYear(editingSurgery.year || null);
      setInitialSurgeryMonth(editingSurgery.month || null);
    }
  }, [editingSurgery]);

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
      screenName: ANALYTICS_SCREEN_NAMES.PROCEDURE_ADD,
    });
  }, []);

  const handleSave = async () => {
    const trimmed = surgeryName.trim();
    if (!trimmed) return;
    setLoading(true);
    try {
      if (editingSurgery && index !== undefined) {
        // Edit
        const updatedSurgery: ISurgery = {
          ...surgeries[index],
          name: trimmed,
          year: surgeryYear,
          month: surgeryMonth,
        };
        const updatedSurgeries = [...surgeries];
        updatedSurgeries[index] = updatedSurgery;
        await saveSurgeries(sortByYearMonth(updatedSurgeries));
      } else {
        // Add
        const newSurgery: ISurgery = {
          name: trimmed,
          year: surgeryYear,
          month: surgeryMonth,
        };
        const updatedSurgeries = [...surgeries, newSurgery];
        await saveSurgeries(sortByYearMonth(updatedSurgeries));
      }

      // Track completion
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
        screenName: ANALYTICS_SCREEN_NAMES.PROCEDURE_ADD_COMPLETE,
      });

      navigate("/profile/health-profile/surgeries");
    } catch (error) {
      showToast({ message: t("errors.toast"), variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const checkValid = () => {
    const trimmedName = surgeryName.trim();
    if (!trimmedName) return false;
    if (surgeryYear === null || surgeryMonth === null) return false;
    if (
      editingSurgery &&
      trimmedName === initialSurgeryName &&
      surgeryYear === initialSurgeryYear &&
      surgeryMonth === initialSurgeryMonth
    )
      return false;
    return true;
  };

  return (
    <div
      className="profile-page-root flex flex-col"
      style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
    >
      <FormHeader module="add_surgeries" memberInfo={memberInfo} />
      <MSpacer />
      <div className="profile-input-container flex-1">
        <MInputField
          label={t("add_surgeries.surgeryName")}
          value={surgeryName}
          placeholder={t("common.notAnswered")}
          onChange={(e) => setSurgeryName(e.target.value)}
        />
        <MDateInputField
          hideDate
          label={t("add_surgeries.surgeryDate")}
          minYear={memberInfo.dobYear || 1940}
          value={
            surgeryYear && surgeryMonth
              ? new Date(surgeryYear, surgeryMonth - 1)
              : undefined
          }
          onDateChange={(date) => {
            setSurgeryYear(date.getFullYear());
            setSurgeryMonth(date.getMonth() + 1);
          }}
          formatDate={(date) =>
            date
              ? `${date.toLocaleString("default", {
                  month: "long",
                })} ${date.getFullYear()}`
              : ""
          }
          placeholder={t("common.notAnswered")}
          size="md"
          datePickerTitle={t("common.selectDate")}
        />
      </div>
      <div className="confirm-footer">
        <MButton
          type="button"
          variant="filled"
          size="lg"
          className="w-full"
          disabled={!checkValid()}
          onClick={handleSave}
          text={t("common.done")}
        />
      </div>
    </div>
  );
};

export default SurgeriesForm;
